
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Maximize2, Minimize2, Clock, AlignLeft, Sparkles, Loader2, Music, Volume2, VolumeX } from 'lucide-react';
import { useFeatureAccess } from '../../../hooks/useFeatureAccess';
import { CustomsGate } from '../../nobel';
import { geminiEngine } from '../../../services/geminiEngine';
import { EssayPrompt } from '../../../types';

export const WritingFlowState: React.FC = () => {
  const userId = sessionStorage.getItem('ielts_user_id') || '';
  const { canAccess, tier } = useFeatureAccess('advanced_module', userId);
  const [paywallOpen, setPaywallOpen] = useState(false);

  const [zenMode, setZenMode] = useState(false);
  const [text, setText] = useState('');
  const [prompt, setPrompt] = useState<EssayPrompt | null>(null);
  const [loading, setLoading] = useState(true);
  const [timer, setTimer] = useState(0); // Seconds
  const [isActive, setIsActive] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(false);

  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (!canAccess) {
      setPaywallOpen(true);
    } else {
      loadPrompt();
    }
  }, [canAccess]);

  useEffect(() => {
    let interval: number;
    if (isActive) {
      interval = window.setInterval(() => setTimer(t => t + 1), 1000);
    }
    return () => clearInterval(interval);
  }, [isActive]);

  // Handle ambient sound
  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio('https://assets.mixkit.co/sfx/preview/mixkit-light-rain-loop-1253.mp3');
      audioRef.current.loop = true;
      audioRef.current.volume = 0.3;
    }

    if (soundEnabled) {
      audioRef.current.play().catch(e => console.log("Audio play failed", e));
    } else {
      audioRef.current.pause();
    }

    return () => {
      if (audioRef.current) audioRef.current.pause();
    };
  }, [soundEnabled]);

  const loadPrompt = async () => {
    setLoading(true);
    try {
      const data = await geminiEngine.generateEssayPrompt();
      setPrompt(data);
      setIsActive(true);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const wordCount = text.trim().split(/\s+/).filter(w => w.length > 0).length;
  const progress = Math.min(100, (wordCount / 250) * 100);

  const formatTime = (s: number) => {
    const mins = Math.floor(s / 60);
    const secs = s % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const toggleZen = () => {
    setZenMode(!zenMode);
    // Request full screen logic could go here
  };

  return (
    <>
      <div className={`h-full transition-all duration-700 flex flex-col ${zenMode ? 'fixed inset-0 z-50 bg-[#F9F8F4]' : 'bg-white rounded-2xl border border-stone-200 shadow-sm relative'}`}>

        {/* Toolbar */}
        <div className={`flex items-center justify-between p-6 border-b border-stone-100 transition-all ${zenMode ? 'max-w-4xl mx-auto w-full' : ''}`}>
          <div className="flex items-center gap-6">
            {zenMode ? (
              <button onClick={toggleZen} className="text-stone-400 hover:text-stone-900 transition-colors">
                <Minimize2 size={20} />
              </button>
            ) : (
              <button onClick={toggleZen} className="text-stone-400 hover:text-[#C5A059] transition-colors flex items-center gap-2">
                <Maximize2 size={18} /> <span className="text-xs font-bold mono uppercase">Zen Mode</span>
              </button>
            )}

            <button onClick={() => setSoundEnabled(!soundEnabled)} className={`flex items-center gap-2 text-xs font-bold mono uppercase transition-colors ${soundEnabled ? 'text-[#C5A059]' : 'text-stone-400'}`}>
              {soundEnabled ? <Volume2 size={18} /> : <VolumeX size={18} />} Ambient
            </button>
          </div>

          <div className="flex items-center gap-6">
            <div className="text-center">
              <div className="text-[8px] mono text-stone-400 uppercase tracking-widest mb-1">Duration</div>
              <div className="font-mono text-stone-800 font-bold">{formatTime(timer)}</div>
            </div>
            <div className="w-px h-8 bg-stone-200"></div>
            <div className="text-center">
              <div className="text-[8px] mono text-stone-400 uppercase tracking-widest mb-1">Words</div>
              <div className={`font-mono font-bold ${wordCount >= 250 ? 'text-emerald-500' : 'text-stone-800'}`}>
                {wordCount} <span className="text-stone-300 text-[10px]">/ 250</span>
              </div>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className={`flex-1 overflow-y-auto custom-scrollbar ${zenMode ? 'flex justify-center' : ''}`}>
          <div className={`p-8 md:p-12 transition-all ${zenMode ? 'max-w-3xl w-full' : 'w-full'}`}>
            {loading ? (
              <div className="flex flex-col items-center justify-center h-64 space-y-4">
                <Loader2 className="animate-spin text-[#C5A059]" size={32} />
                <p className="text-stone-400 mono text-xs uppercase">Calibrating Zen Environment...</p>
              </div>
            ) : (
              <>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-12"
                >
                  <div className="text-[10px] font-black text-[#C5A059] mono uppercase tracking-[0.2em] mb-4">Prompt</div>
                  <h2 className="text-2xl md:text-3xl font-serif text-stone-800 leading-tight">
                    {prompt?.question || "Describe a memorable journey you have taken."}
                  </h2>
                </motion.div>

                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Start typing... The flow will follow."
                  className="w-full h-[60vh] resize-none outline-none text-lg md:text-xl leading-relaxed text-stone-600 placeholder:text-stone-300 font-serif bg-transparent"
                  autoFocus
                />
              </>
            )}
          </div>
        </div>

        {/* Progress Line */}
        <div className="h-1 bg-stone-100 w-full">
          <motion.div
            className="h-full bg-gradient-to-r from-[#C5A059] to-amber-300"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ type: 'spring', stiffness: 50 }}
          />
        </div>
      </div>

      <PaywallModal
        isOpen={paywallOpen}
        onClose={() => setPaywallOpen(false)}
        feature="Writing Flow State"
        reason="premium_feature"
        currentTier={tier}
      />
    </>
  );
};

export default WritingFlowState;
